/**
 * 失踪儿童浏览页面 - 宝贝回家
 */

import { useState, useEffect } from 'react';
import type { MissingChild, ChildFilter } from '../types';
import { getMissingChildren, getProvinces } from '../services/antiTraffic';

export default function ChildrenPage() {
  const [children, setChildren] = useState<MissingChild[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<ChildFilter>({});
  const [showFilter, setShowFilter] = useState(false);

  // 加载数据
  useEffect(() => {
    loadChildren();
  }, [filter]);

  const loadChildren = async () => {
    setIsLoading(true);
    try {
      const result = await getMissingChildren(filter);
      setChildren(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('[宝贝] 加载失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 获取筛选标签
  const getFilterTags = () => {
    const tags: string[] = [];
    if (filter.province) tags.push(filter.province);
    if (filter.gender) tags.push(filter.gender === '男' ? '男孩' : '女孩');
    if (filter.ageMin || filter.ageMax) {
      if (filter.ageMin && filter.ageMax) {
        tags.push(`${filter.ageMin}-${filter.ageMax}岁`);
      } else if (filter.ageMin) {
        tags.push(`${filter.ageMin}岁以上`);
      } else {
        tags.push(`${filter.ageMax}岁以下`);
      }
    }
    return tags;
  };

  return (
    <div className="min-h-screen bg-bg-dark">
      {/* 头部 */}
      <header className="sticky top-0 bg-bg-dark/95 backdrop-blur-sm border-b border-white/10 p-4 z-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
              <span>👶</span>
              <span>宝贝回家</span>
            </h1>
            <p className="text-sm text-text-muted">
              扩散一次，团圆希望 {total} 位儿童等待回家
            </p>
          </div>

          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`px-4 py-2 rounded-lg border ${
              showFilter
                ? 'bg-sos-red border-sos-red text-white'
                : 'border-white/20 text-text-muted'
            }`}
          >
            筛选
          </button>
        </div>

        {/* 筛选面板 */}
        {showFilter && (
          <div className="bg-bg-light rounded-xl p-4 mt-3 space-y-4 animate-fade-in">
            {/* 省份 */}
            <div>
              <label className="text-sm text-text-muted mb-2 block">省份</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter({ ...filter, province: undefined })}
                  className={`px-3 py-1 rounded-full text-sm ${
                    !filter.province
                      ? 'bg-sos-red text-white'
                      : 'bg-white/10 text-text-muted'
                  }`}
                >
                  全部
                </button>
                {getProvinces().map(p => (
                  <button
                    key={p}
                    onClick={() => setFilter({ ...filter, province: p })}
                    className={`px-3 py-1 rounded-full text-sm ${
                      filter.province === p
                        ? 'bg-sos-red text-white'
                        : 'bg-white/10 text-text-muted'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* 性别 */}
            <div>
              <label className="text-sm text-text-muted mb-2 block">性别</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilter({ ...filter, gender: undefined })}
                  className={`px-4 py-1 rounded-full text-sm ${
                    !filter.gender
                      ? 'bg-sos-red text-white'
                      : 'bg-white/10 text-text-muted'
                  }`}
                >
                  全部
                </button>
                <button
                  onClick={() => setFilter({ ...filter, gender: '男' })}
                  className={`px-4 py-1 rounded-full text-sm ${
                    filter.gender === '男'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/10 text-text-muted'
                  }`}
                >
                  👦 男孩
                </button>
                <button
                  onClick={() => setFilter({ ...filter, gender: '女' })}
                  className={`px-4 py-1 rounded-full text-sm ${
                    filter.gender === '女'
                      ? 'bg-pink-500 text-white'
                      : 'bg-white/10 text-text-muted'
                  }`}
                >
                  👧 女孩
                </button>
              </div>
            </div>

            {/* 年龄 */}
            <div>
              <label className="text-sm text-text-muted mb-2 block">年龄</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="最小"
                  value={filter.ageMin || ''}
                  onChange={e => setFilter({ ...filter, ageMin: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-20 px-3 py-1 rounded-lg bg-white/10 text-text-primary text-sm"
                />
                <span className="text-text-muted">-</span>
                <input
                  type="number"
                  placeholder="最大"
                  value={filter.ageMax || ''}
                  onChange={e => setFilter({ ...filter, ageMax: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-20 px-3 py-1 rounded-lg bg-white/10 text-text-primary text-sm"
                />
                <span className="text-text-muted text-sm">岁</span>
              </div>
            </div>

            {/* 重置 */}
            <button
              onClick={() => setFilter({})}
              className="w-full py-2 text-sm text-text-muted hover:text-sos-red"
            >
              清除所有筛选
            </button>
          </div>
        )}

        {/* 当前筛选标签 */}
        {getFilterTags().length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {getFilterTags().map((tag, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded-full bg-sos-red/20 text-sos-red text-xs"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* 内容 */}
      <main className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-sos-red border-t-transparent" />
          </div>
        ) : children.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="text-lg font-medium text-text-primary mb-2">暂无数据</h2>
            <p className="text-text-muted text-sm">试试调整筛选条件</p>
          </div>
        ) : (
          <div className="space-y-4">
            {children.map(child => (
              <ChildCard key={child.id} child={child} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// 儿童卡片组件
function ChildCard({ child }: { child: MissingChild }) {
  // 格式化时间
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  // 转发功能
  const handleShare = () => {
    const text = `【紧急寻人】${child.name}，${child.currentAge}岁${child.gender}。${child.features}。失踪地点：${child.missingLocation}。如有线索请拨打：${child.contactPhone}`;
    if (navigator.share) {
      navigator.share({
        title: `寻找${child.name}`,
        text: text,
        url: window.location.href
      });
    } else {
      // 复制到剪贴板
      navigator.clipboard.writeText(text);
      alert('信息已复制到剪贴板，可粘贴分享');
    }
  };

  // 拨打电话
  const handleCall = () => {
    const phone = child.contactPhone.replace(/\*+/g, '');
    window.location.href = `tel:${phone}`;
  };

  // 一键报警
  const handlePolice = () => {
    window.location.href = 'tel:110';
  };

  return (
    <div className="bg-bg-light rounded-xl overflow-hidden">
      <div className="flex p-4">
        {/* 照片 */}
        <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-white/10">
          <img
            src={child.photoUrl}
            alt={child.name}
            className="w-full h-full object-cover"
            onError={e => {
              e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23666" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="40">?</text></svg>';
            }}
          />
        </div>

        {/* 信息 */}
        <div className="flex-1 ml-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-text-primary text-lg">{child.name}</h3>
              <p className="text-sm text-text-muted">
                {child.gender} | {child.currentAge}岁 | {child.province}
              </p>
            </div>
            <span className={`px-2 py-0.5 rounded text-xs ${
              child.platform === '宝贝回家'
                ? 'bg-info/20 text-info'
                : child.platform === '团圆系统'
                ? 'bg-sos-red/20 text-sos-red'
                : 'bg-warning/20 text-warning'
            }`}>
              {child.platform}
            </span>
          </div>

          <div className="mt-2 text-sm">
            <p className="text-text-muted">
              <span className="text-text-muted/60">失踪日期：</span>
              {formatDate(child.missingDate)}
            </p>
            <p className="text-text-muted truncate">
              <span className="text-text-muted/60">失踪地点：</span>
              {child.missingLocation}
            </p>
          </div>
        </div>
      </div>

      {/* 特征 */}
      <div className="px-4 pb-3">
        <p className="text-sm text-text-muted/80 bg-white/5 rounded-lg p-2">
          📋 {child.features}
        </p>
      </div>

      {/* 操作 */}
      <div className="flex border-t border-white/10">
        <button
          onClick={handleShare}
          className="flex-1 py-3 text-center text-sm text-success hover:bg-success/10 flex items-center justify-center gap-1"
        >
          <span>📤</span>
          <span>转发</span>
        </button>
        <button
          onClick={handleCall}
          className="flex-1 py-3 text-center text-sm text-info hover:bg-info/10 flex items-center justify-center gap-1"
        >
          <span>📞</span>
          <span>联系家属</span>
        </button>
        <button
          onClick={handlePolice}
          className="flex-1 py-3 text-center text-sm text-sos-red hover:bg-sos-red/10 flex items-center justify-center gap-1"
        >
          <span>🚨</span>
          <span>报警</span>
        </button>
      </div>
    </div>
  );
}
